import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import { Observable, map } from 'rxjs';
import { withCollectionRequestContext } from '../interceptors/collection-request-context';
import { StatementOriginRequiredInputConstraint } from '../interfaces/collection-revision.interface';
import {
  CollectionRevisionScope,
  StatementOriginCatalogPage,
  StatementOriginCatalogQuery,
  StatementOriginRow,
  StatementOriginScope,
} from '../interfaces/statement-origin.interface';
import {
  buildCollectionRevisionStatementOriginUrl,
  buildCollectionRevisionStatementOriginsUrl,
} from './collection-revision-urls';
import { CollectionRevisionWireContractError } from './collection-revision.service';

/**
 * Read-only client for the Collections statement-origin catalog facade
 * (Chordium Task 4). Revision-scoped, GET-only, no idempotency, no
 * team scope parameter — the effective team is derived server-side from the
 * collection. `constraint` is always sent (even `ALL_VISIBLE`); `search` is
 * omitted when blank and trimmed otherwise; `page`/`size` are always sent.
 */
@Injectable({ providedIn: 'root' })
export class CollectionRevisionStatementOriginService {
  private readonly http = inject(HttpClient);
  private readonly apiRoot = `${environment.apiUrl}/api/chordium/v1`;

  list(
    scope: CollectionRevisionScope,
    query: StatementOriginCatalogQuery,
  ): Observable<StatementOriginCatalogPage> {
    const exactScope = requireScope(scope);
    const params = buildListParams(query);
    return this.http
      .get<unknown>(
        buildCollectionRevisionStatementOriginsUrl(
          this.apiRoot,
          exactScope.collectionId,
          exactScope.revisionId,
        ),
        {
          params,
          context: withCollectionRequestContext(exactScope.collectionId),
        },
      )
      .pipe(map((response) => decodeStatementOriginCatalogPage(response)));
  }

  get(
    scope: CollectionRevisionScope,
    statementOriginId: string,
  ): Observable<StatementOriginRow> {
    const exactScope = requireScope(scope);
    const exactId = requireNonEmpty(statementOriginId, 'statementOriginId');
    return this.http
      .get<unknown>(
        buildCollectionRevisionStatementOriginUrl(
          this.apiRoot,
          exactScope.collectionId,
          exactScope.revisionId,
          exactId,
        ),
        { context: withCollectionRequestContext(exactScope.collectionId) },
      )
      .pipe(map((response) => decodeStatementOriginRow(response)));
  }
}

const STATEMENT_ORIGIN_ROW_KEYS = [
  'id',
  'entity_profile_id',
  'scope',
  'display_name',
  'icon_hash',
  'icon_url',
  'description',
  'website_url',
] as const;

const STATEMENT_ORIGIN_CATALOG_PAGE_KEYS = [
  'items',
  'page',
  'size',
  'total_elements',
  'total_pages',
  'constraint',
  'allow_create',
] as const;

function buildListParams(
  query: StatementOriginCatalogQuery,
): Record<string, string> {
  const constraint = requireConstraint(query.constraint);
  const page = requireNonNegativeInteger(query.page, 'page');
  const size = requireSize(query.size);
  const params: Record<string, string> = {
    constraint,
    page: String(page),
    size: String(size),
  };
  const search = query.search?.trim();
  if (search) {
    params['search'] = search;
  }
  return params;
}

function decodeStatementOriginCatalogPage(
  value: unknown,
): StatementOriginCatalogPage {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, STATEMENT_ORIGIN_CATALOG_PAGE_KEYS) ||
    !Array.isArray(value['items']) ||
    !Number.isInteger(value['page']) ||
    (value['page'] as number) < 0 ||
    !Number.isInteger(value['size']) ||
    (value['size'] as number) < 1 ||
    !Number.isInteger(value['total_elements']) ||
    (value['total_elements'] as number) < 0 ||
    !Number.isInteger(value['total_pages']) ||
    (value['total_pages'] as number) < 0 ||
    !isStatementOriginConstraint(value['constraint']) ||
    typeof value['allow_create'] !== 'boolean'
  ) {
    throw wireError('Statement origin catalog page envelope is invalid.');
  }
  const items = Object.freeze(
    value['items'].map((item) => decodeStatementOriginRow(item)),
  );
  return Object.freeze({
    items,
    page: value['page'] as number,
    size: value['size'] as number,
    total_elements: value['total_elements'] as number,
    total_pages: value['total_pages'] as number,
    constraint: value['constraint'] as StatementOriginRequiredInputConstraint,
    allow_create: value['allow_create'] as boolean,
  });
}

function decodeStatementOriginRow(value: unknown): StatementOriginRow {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, STATEMENT_ORIGIN_ROW_KEYS) ||
    !isNonEmptyString(value['id']) ||
    !isNonEmptyString(value['entity_profile_id']) ||
    !isStatementOriginScope(value['scope']) ||
    !isNonEmptyString(value['display_name']) ||
    !isNullableString(value['icon_hash']) ||
    !isNullableString(value['icon_url']) ||
    !isNullableString(value['description']) ||
    !isNullableString(value['website_url'])
  ) {
    throw wireError('Statement origin row is invalid.');
  }
  return Object.freeze({
    id: value['id'],
    entity_profile_id: value['entity_profile_id'],
    scope: value['scope'] as StatementOriginScope,
    display_name: value['display_name'],
    icon_hash: value['icon_hash'] as string | null,
    icon_url: value['icon_url'] as string | null,
    description: value['description'] as string | null,
    website_url: value['website_url'] as string | null,
  });
}

function requireScope(scope: CollectionRevisionScope): CollectionRevisionScope {
  return Object.freeze({
    collectionId: requireNonEmpty(scope.collectionId, 'collectionId'),
    revisionId: requireNonEmpty(scope.revisionId, 'revisionId'),
  });
}

function requireConstraint(
  value: StatementOriginRequiredInputConstraint,
): StatementOriginRequiredInputConstraint {
  if (!isStatementOriginConstraint(value)) {
    throw new TypeError('constraint must be one of ALL_VISIBLE or BRAZIL_PRO');
  }
  return value;
}

function requireNonNegativeInteger(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(`${field} must be a non-negative integer`);
  }
  return value;
}

function requireSize(value: number): number {
  if (!Number.isInteger(value) || value < 1 || value > 1000) {
    throw new TypeError('size must be an integer between 1 and 1000');
  }
  return value;
}

function requireNonEmpty(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function isStatementOriginConstraint(
  value: unknown,
): value is StatementOriginRequiredInputConstraint {
  return value === 'ALL_VISIBLE' || value === 'BRAZIL_PRO';
}

function isStatementOriginScope(value: unknown): value is StatementOriginScope {
  return value === 'SYSTEM' || value === 'TEAM';
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(value);
  const expectedSet = new Set(expected);
  return (
    keys.length === expected.length && keys.every((key) => expectedSet.has(key))
  );
}

function wireError(message: string): CollectionRevisionWireContractError {
  return new CollectionRevisionWireContractError(message);
}
